import { Box } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";

import { useCan } from "../../auth/permissions.js";
import { SectionHeader, type SectionTab } from "../../components/SectionHeader.js";
import {
  HeaderActionContext,
  useHeaderActionTarget,
} from "../support/HeaderActionSlot.js";
import { MarketingAgentControl } from "./components/MarketingAgentControl.js";

/**
 * Marketing section shell. The section holds TWO top-level areas, switched
 * by the `<SectionHeader>` tabs:
 *
 *   - "AI Agent"        — the autonomous marketing agent (`/marketing/agent`
 *                          → Workbench / History / Settings, rendered by
 *                          `MarketingAgentLayout` with its own secondary
 *                          sub-tab strip).
 *   - "Email Campaigns" — the manual email tool relocated out of the CRM
 *                          on 2026-06-02 (`/marketing/campaigns`, CRM-runtime
 *                          backed via `MarketingCampaignsRoot`).
 *   - "Webinars"        — the five-step event flow (`/marketing/webinars`):
 *                          invite → who joined → blog/LinkedIn → public
 *                          survey → leads. Dashboard runtime; see
 *                          docs/webinars.md.
 *
 * The header's right cluster is lean (2026-06-16): just **Pause / Resume**,
 * rendered ONLY in the agent area (it makes no sense over the email tool). The
 * budget pill + the Mode (autonomy) menu were removed and **steering moved into
 * the Workbench's Autonomous-drafting tab**; autonomy is always review-gated.
 * The `<HeaderAction>` portal slot (the empty `<Box>` first in the cluster)
 * stays mounted in both areas so a sub-page can still inject an action.
 *
 * The "Email Campaigns" tab requires `crm:view` — the real floor for
 * minting a CRM session via /api/crm/session. `super_admin` + `sales_manager`
 * hold both `marketing:view` and `crm:view`, so they see the tab. The
 * `marketing_manager` role (added 2026-06-08) holds `marketing:view` but NOT
 * `crm:view`, so for them this tab is hidden (never broken) — they work in the
 * AI Agent area. An admin can layer `crm:view` on via the Roles matrix to
 * surface campaigns (that also reveals the CRM tab, by the same `crm:view` gate).
 */
/**
 * When the "NEW" flag on the Webinars tab stops showing.
 *
 * Shipped 2026-08-07; a badge that never comes off is decoration everyone
 * learns to ignore, so this one removes itself. Delete the flag and this
 * constant together once it lapses.
 */
export const NEW_BADGE_UNTIL = Date.UTC(2026, 9, 7); // 2026-10-07

/** Whether the "NEW" flag should still be shown. Pure so it is testable
 * without freezing the clock in a component test. */
export function showsNewBadge(now: number = Date.now()): boolean {
  return now < NEW_BADGE_UNTIL;
}

export function MarketingLayout(): JSX.Element {
  const [slotEl, setSlotEl] = useHeaderActionTarget();
  const { pathname } = useLocation();
  // Pause/Resume belongs only to the autonomous AI Agent area — not the email
  // tools (Email Campaigns / Cold Email Agent).
  const onAgentArea = pathname.startsWith("/marketing/agent");
  const canSeeCampaigns = useCan("crm:view");
  // The Cold Email Agent is a company-wide outreach tool: crm-api only lets an
  // all-records actor (super_admin or a crm:view_all holder like
  // marketing_manager) build/run it. Gate the tab on crm:view_all so a
  // record-scoped role (e.g. sales_manager) doesn't see a tab that would 403.
  const canSeeColdEmail = useCan("crm:view_all");

  const tabs: SectionTab[] = [
    { to: "agent", label: "AI Agent" },
    ...(canSeeCampaigns ? [{ to: "campaigns", label: "Email Campaigns" }] : []),
    // Webinars needs only `marketing:view` — the flow degrades gracefully
    // without CRM access (the steps that create a campaign or push a lead
    // explain what is missing instead of 403-ing), so it is not gated on
    // `crm:view` the way the campaign tabs are.
    {
      to: "webinars",
      label: "Webinars",
      ...(showsNewBadge() ? { badge: "New" } : {}),
    },
    ...(canSeeColdEmail ? [{ to: "cold-email", label: "Cold Email Agent" }] : []),
  ];

  return (
    <HeaderActionContext.Provider value={slotEl}>
      <>
        <SectionHeader
          title="Marketing"
          tabs={tabs}
          tabsAriaLabel="Marketing areas"
          right={
            <>
              <Box ref={setSlotEl} sx={{ display: "flex", alignItems: "center", gap: 1 }} />
              {/* Lean header (2026-06-16): just Pause/Resume — the budget pill +
                  the Mode (autonomy) menu were removed, and steering moved into
                  the Workbench's Autonomous-drafting tab. Mode is always
                  review-gated now. */}
              {onAgentArea && <MarketingAgentControl />}
            </>
          }
        />
        <Outlet />
      </>
    </HeaderActionContext.Provider>
  );
}
